import { useState, useCallback, useEffect } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { getEmployees, createEmployee, deleteEmployee, getRoles, getDepartments } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Form } from '../../components/ui/form';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '../../components/ui/pagination';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader,
  DialogTitle, DialogTrigger, DialogFooter,
} from '../../components/ui/dialog';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { Avatar, AvatarFallback } from '../../components/ui/avatar';
import {
  Search, Plus, Filter, MoreHorizontal, Eye, EyeOff, Edit, Trash2,
  Download, Phone, Mail, Shield, RefreshCw, AlertCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  EditEmployeeModal,
} from '../../components/ui/ActionModals';
import {
  EMPLOYEE_DEFAULT_VALUES,
  buildEmployeeCreatePayload,
  employeeCreateSchema,
  getEntityErrorMessage,
  mapEntityApiErrors,
} from '../../lib/entityFormValidation';
import { canImpersonateUser, getDashboardPathForRole } from '../../lib/auth';

const statusColors = {
  Active: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-300',
  'On Leave': 'bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300',
  Inactive: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300',
};

const statusApiToDisplay = (s) => {
  if (!s) return 'Active';
  const v = String(s).toLowerCase();
  if (v === 'inactive') return 'Inactive';
  if (v === 'suspended') return 'On Leave';
  return 'Active';
};

const isEmployeePlanLimitError = (message) =>
  typeof message === 'string' && message.toLowerCase().includes('employee limit reached for your plan');

const statusDisplayToApi = (value) => {
  if (value === 'Active') return 'active';
  if (value === 'Inactive') return 'inactive';
  if (value === 'On Leave') return 'suspended';
  return '';
};

const buildPaginationItems = (page, totalPages) => {
  const pages = [];
  for (let current = 1; current <= totalPages; current += 1) {
    if (current === 1 || current === totalPages || Math.abs(current - page) <= 1) {
      pages.push(current);
    } else if (pages[pages.length - 1] !== '...') {
      pages.push('...');
    }
  }
  return pages;
};

function TablePagination({ page, totalPages, onPageChange }) {
  if (totalPages <= 1) return null;
  const items = buildPaginationItems(page, totalPages);
  return (
    <Pagination className="justify-end">
      <PaginationContent>
        <PaginationItem>
          <PaginationPrevious
            href="#"
            onClick={(event) => {
              event.preventDefault();
              if (page > 1) onPageChange(page - 1);
            }}
            className={page <= 1 ? 'pointer-events-none opacity-50' : ''}
          />
        </PaginationItem>
        {items.map((item, index) => (
          <PaginationItem key={`${item}-${index}`}>
            {item === '...' ? (
              <PaginationEllipsis />
            ) : (
              <PaginationLink
                href="#"
                isActive={item === page}
                onClick={(event) => {
                  event.preventDefault();
                  onPageChange(item);
                }}
              >
                {item}
              </PaginationLink>
            )}
          </PaginationItem>
        ))}
        <PaginationItem>
          <PaginationNext
            href="#"
            onClick={(event) => {
              event.preventDefault();
              if (page < totalPages) onPageChange(page + 1);
            }}
            className={page >= totalPages ? 'pointer-events-none opacity-50' : ''}
          />
        </PaginationItem>
      </PaginationContent>
    </Pagination>
  );
}

const EmployeesTable = ({ employees: propEmployees = [], title, description }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [modal, setModal] = useState(null); // 'view' | 'edit' | 'delete'
  const [apiEmployees, setApiEmployees] = useState(null);
  const [roles, setRoles] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 10,
    total: 0,
    totalPages: 1,
  });
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [reassignEmployeeId, setReassignEmployeeId] = useState('');
  const [fetchError, setFetchError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [impersonatingId, setImpersonatingId] = useState('');
  const [submitError, setSubmitError] = useState('');
  const addEmployeeForm = useForm({
    resolver: zodResolver(employeeCreateSchema),
    defaultValues: EMPLOYEE_DEFAULT_VALUES,
    mode: 'onBlur',
    reValidateMode: 'onChange',
  });
  const addForm = addEmployeeForm.watch();
  const setAddForm = (updater) => {
    const currentValues = addEmployeeForm.getValues();
    const nextValues = typeof updater === 'function' ? updater(currentValues) : updater;
    Object.entries(nextValues).forEach(([key, value]) => {
      addEmployeeForm.setValue(key, value, {
        shouldDirty: true,
        shouldValidate: true,
      });
    });
  };
  const { token, user, startImpersonation } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const basePath = location.pathname.startsWith('/whitelabel')
    ? '/whitelabel'
    : location.pathname.startsWith('/employee')
      ? '/employee'
      : '/admin';

  const fetchRolesAndDepartments = useCallback(async () => {
    if (!token) return;
    try {
      const [rolesRes, deptRes] = await Promise.all([getRoles(token), getDepartments(token)]);
      setRoles(rolesRes?.data?.items ?? []);
      setDepartments(deptRes?.data?.items ?? []);
    } catch {
      setRoles([]);
      setDepartments([]);
    }
  }, [token]);

  const fetchEmployees = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const resp = await getEmployees(token, {
        q: searchTerm.trim() || undefined,
        status: statusDisplayToApi(statusFilter) || undefined,
        page,
        pageSize: 10,
      });
      const items = resp?.data?.items ?? [];
      const meta = resp?.data?.pagination || {};
      const mapped = items.map((u) => ({
        id: u.id,
        name: u.full_name,
        email: u.email,
        roleKey: 'employee',
        phone: u.phone || '—',
        role: u.role_name ?? '—',
        department: u.department_name ?? '—',
        employee_role_id: u.employee_role_id ?? '',
        department_id: u.department_id ?? '',
        createdById: u.created_by_id ?? '',
        whiteLabelId: u.white_label_id ?? null,
        status: statusApiToDisplay(u.status),
        joinDate: u.created_at ? new Date(u.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—',
        createdAgents: Number(u.created_agents_count) || 0,
        createdLoanApplications: Number(u.created_loan_applications_count) || 0,
        assignedLeads: Number(u.assigned_leads_count) || 0,
        assignedLoans: Number(u.assigned_loans_count) || 0,
        processedFiles: Number(u.converted_leads_count) || 0,
        performance: (Number(u.created_leads_count) || 0) > 0
          ? Math.round(((Number(u.converted_leads_count) || 0) / (Number(u.created_leads_count) || 1)) * 100)
          : 0,
      }));
      setApiEmployees(mapped);
      setPagination({
        page: Number(meta.page || page),
        pageSize: Number(meta.page_size || 10),
        total: Number(meta.total || mapped.length || 0),
        totalPages: Number(meta.total_pages || 1),
      });
      setFetchError('');
    } catch (err) {
      setApiEmployees(null);
      setPagination({ page: 1, pageSize: 10, total: 0, totalPages: 1 });
      setFetchError(err?.message || 'Failed to load employees. Please try again later.');
    } finally {
      setLoading(false);
    }
  }, [page, searchTerm, statusFilter, token]);

  useEffect(() => {
    fetchEmployees();
  }, [fetchEmployees]);
  useEffect(() => {
    fetchRolesAndDepartments();
  }, [fetchRolesAndDepartments]);

  useEffect(() => {
    setPage(1);
  }, [searchTerm, statusFilter]);

  useEffect(() => {
    const subscription = addEmployeeForm.watch(() => {
      if (submitError) {
        setSubmitError('');
      }
    });
    return () => subscription.unsubscribe();
  }, [addEmployeeForm, submitError]);

  const employees = apiEmployees ?? propEmployees;

  const openModal = (emp, type) => {
    setSelectedEmployee(emp);
    setModal(type);
    if (type === 'delete') {
      setReassignEmployeeId('');
    }
  };
  const closeModal = () => {
    setModal(null);
    setSelectedEmployee(null);
    setReassignEmployeeId('');
  };

  const filteredEmployees = employees;
  const canImpersonateEmployees = user?.role === 'super_admin' || user?.role === 'wl_admin';

  const handleImpersonateEmployee = async (employee) => {
    setImpersonatingId(employee.id);
    try {
      const result = await startImpersonation(employee.id);
      if (!result?.success || !result?.user) {
        throw new Error(result?.error || 'Failed to start impersonation');
      }
      window.location.assign(getDashboardPathForRole(result.user.role));
    } catch (err) {
      toast.error(err?.message || 'Failed to start impersonation');
    } finally {
      setImpersonatingId('');
    }
  };

  const handleAddEmployee = async (values) => {
    if (!token) {
      toast.success('Employee added locally (no API)');
      setShowAddDialog(false);
      return;
    }
    setSubmitError('');
    setSubmitting(true);
    try {
      await createEmployee(token, buildEmployeeCreatePayload(values));
      toast.success('Employee added successfully!');
      setShowAddDialog(false);
      addEmployeeForm.reset(EMPLOYEE_DEFAULT_VALUES);
      await fetchEmployees();
    } catch (err) {
      const fieldErrors = mapEntityApiErrors(err);
      Object.entries(fieldErrors).forEach(([fieldName, message]) => {
        addEmployeeForm.setError(fieldName, { type: 'server', message });
      });
      const firstField = Object.keys(fieldErrors)[0];
      if (firstField) {
        addEmployeeForm.setFocus(firstField);
      }

      const message = getEntityErrorMessage(err, 'Failed to add employee.');
      setSubmitError(message);
      if (user?.role === 'wl_admin' && isEmployeePlanLimitError(message)) {
        setShowAddDialog(false);
        window.setTimeout(() => {
          toast.error(message, {
            action: {
              label: 'Upgrade Plan',
              onClick: () => navigate('/whitelabel/upgrade-plan'),
            },
          });
        }, 0);
      } else {
        toast.error(message);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="employees-management">
      {/* Production Modals */}
      <EditEmployeeModal employee={selectedEmployee} open={modal === 'edit'} onClose={() => { closeModal(); fetchEmployees(); }} token={token} roles={roles} departments={departments} />
      <Dialog open={modal === 'delete'} onOpenChange={(open) => { if (!open) closeModal(); }}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Delete Employee</DialogTitle>
            <DialogDescription>
              Delete employee "{selectedEmployee?.name}". If they have agents, select another employee to reassign those agents first.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label>Reassign Agents To (optional if no agents)</Label>
            <Select
              value={reassignEmployeeId || '_none'}
              onValueChange={(v) => setReassignEmployeeId(v === '_none' ? '' : v)}
            >
              <SelectTrigger><SelectValue placeholder="Select employee" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="_none">No reassignment</SelectItem>
                {employees
                  .filter((e) => e.id !== selectedEmployee?.id)
                  .map((e) => (
                    <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={closeModal}>Cancel</Button>
            <Button
              type="button"
              className="bg-red-600 hover:bg-red-700 text-white"
              disabled={deleting}
              onClick={async () => {
                if (!token || !selectedEmployee?.id) return;
                setDeleting(true);
                try {
                  await deleteEmployee(token, selectedEmployee.id, reassignEmployeeId || null);
                  setApiEmployees((prev) => (prev || []).filter((e) => e.id !== selectedEmployee.id));
                  toast.success('Employee deleted successfully');
                  closeModal();
                } catch (err) {
                  toast.error(err?.message || 'Failed to delete employee');
                } finally {
                  setDeleting(false);
                }
              }}
            >
              {deleting ? 'Deleting...' : 'Delete Employee'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold font-['Plus_Jakarta_Sans']">
            {title || 'Employees Management'}
          </h1>
          <p className="text-muted-foreground mt-1">
            {description || 'Manage your team and track their activities'}
          </p>
        </div>
        <Dialog open={showAddDialog} onOpenChange={(open) => {
          setShowAddDialog(open);
          if (open) {
            addEmployeeForm.reset(EMPLOYEE_DEFAULT_VALUES);
            setSubmitError('');
            setShowPassword(false);
          }
        }}>
          <DialogTrigger asChild>
            <Button className="bg-emerald-600 hover:bg-emerald-700" data-testid="add-employee-btn">
              <Plus className="h-4 w-4 mr-2" />Add Employee
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle>Add New Employee</DialogTitle>
              <DialogDescription>Enter the employee details below.</DialogDescription>
            </DialogHeader>
            <Form {...addEmployeeForm}>
              <form onSubmit={addEmployeeForm.handleSubmit(handleAddEmployee)} className="space-y-4" noValidate>
              {submitError ? (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{submitError}</AlertDescription>
                </Alert>
              ) : null}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Full Name</Label>
                  <Input id="name" placeholder="Enter name" value={addForm.name} onChange={e => setAddForm(f => ({ ...f, name: e.target.value }))} required />
                  {addEmployeeForm.formState.errors.name ? <p className="text-[0.8rem] font-medium text-destructive">{addEmployeeForm.formState.errors.name.message}</p> : null}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone">Phone</Label>
                  <Input id="phone" placeholder="Enter phone" value={addForm.phone} onChange={e => setAddForm(f => ({ ...f, phone: e.target.value }))} />
                  {addEmployeeForm.formState.errors.phone ? <p className="text-[0.8rem] font-medium text-destructive">{addEmployeeForm.formState.errors.phone.message}</p> : null}
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input id="email" type="email" placeholder="Enter email" value={addForm.email} onChange={e => setAddForm(f => ({ ...f, email: e.target.value }))} required />
                {addEmployeeForm.formState.errors.email ? <p className="text-[0.8rem] font-medium text-destructive">{addEmployeeForm.formState.errors.email.message}</p> : null}
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Use a strong password"
                    value={addForm.password}
                    onChange={e => setAddForm(f => ({ ...f, password: e.target.value }))}
                    required
                    className="pr-10"
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    tabIndex="-1"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                {addEmployeeForm.formState.errors.password ? <p className="text-[0.8rem] font-medium text-destructive">{addEmployeeForm.formState.errors.password.message}</p> : null}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Role</Label>
                  <Select value={addForm.employee_role_id || '_none'} onValueChange={v => setAddForm(f => ({ ...f, employee_role_id: v === '_none' ? '' : v }))}>
                    <SelectTrigger><SelectValue placeholder="Select role" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_none">— None —</SelectItem>
                      {roles.map(r => (
                        <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {addEmployeeForm.formState.errors.employee_role_id ? <p className="text-[0.8rem] font-medium text-destructive">{addEmployeeForm.formState.errors.employee_role_id.message}</p> : null}
                </div>
                <div className="space-y-2">
                  <Label>Department</Label>
                  <Select value={addForm.department_id || '_none'} onValueChange={v => setAddForm(f => ({ ...f, department_id: v === '_none' ? '' : v }))}>
                    <SelectTrigger><SelectValue placeholder="Select dept" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_none">— None —</SelectItem>
                      {departments.map(d => (
                        <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {addEmployeeForm.formState.errors.department_id ? <p className="text-[0.8rem] font-medium text-destructive">{addEmployeeForm.formState.errors.department_id.message}</p> : null}
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setShowAddDialog(false)}>Cancel</Button>
                <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700" disabled={submitting}>
                  {submitting ? 'Saving...' : 'Save Employee'}
                </Button>
              </DialogFooter>
              </form>
            </Form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters */}
      <Card className="border border-border">
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by name or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-employees-input"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-[180px]" data-testid="employees-status-filter">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue placeholder="Filter status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="Active">Active</SelectItem>
                <SelectItem value="On Leave">On Leave</SelectItem>
                <SelectItem value="Inactive">Inactive</SelectItem>
              </SelectContent>
            </Select>
          
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="border border-border">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Employee</TableHead>
                  <TableHead>Contact</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Department</TableHead>
                  <TableHead>Created Agents</TableHead>
                  <TableHead>Loan Applications</TableHead>
                  <TableHead>Assigned Loans</TableHead>
                  <TableHead>Assigned Leads</TableHead>
                  <TableHead>Converted</TableHead>
                  <TableHead>Performance</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredEmployees.map((emp) => (
                  <TableRow key={emp.id} data-testid={`employee-row-${emp.id}`} className="hover:bg-muted/30 transition-colors">
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <Avatar className="h-9 w-9">
                          <AvatarFallback>
                            {emp.name.split(' ').map((n) => n[0]).join('')}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <p className="font-medium">{emp.name}</p>
                          <p className="text-xs text-muted-foreground">Joined {emp.joinDate}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col gap-1">
                        <span className="flex items-center gap-1 text-sm">
                          <Phone className="h-3 w-3 text-muted-foreground" />{emp.phone}
                        </span>
                        <span className="flex items-center gap-1 text-xs text-muted-foreground">
                          <Mail className="h-3 w-3" />{emp.email}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>{emp.role}</TableCell>
                    <TableCell>{emp.department}</TableCell>
                    <TableCell className="font-medium">{emp.createdAgents ?? 0}</TableCell>
                    <TableCell className="font-medium">{emp.createdLoanApplications ?? 0}</TableCell>
                    <TableCell className="font-medium">{emp.assignedLoans}</TableCell>
                    <TableCell className="font-medium">{emp.assignedLeads}</TableCell>
                    <TableCell className="font-medium text-emerald-600">{emp.processedFiles}</TableCell>
                    <TableCell>
                      <div className="w-20">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs font-medium">{emp.performance}%</span>
                        </div>
                        <Progress value={emp.performance} className="h-2" />
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className={statusColors[emp.status]}>{emp.status}</Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-48">
                          <DropdownMenuItem onClick={() => navigate(`${basePath}/employees/${emp.id}/view`)} className="gap-2 cursor-pointer">
                            <Eye className="h-4 w-4 text-blue-500" /><span>View Profile</span>
                          </DropdownMenuItem>
                          {canImpersonateEmployees && canImpersonateUser(user, emp) ? (
                            <DropdownMenuItem
                              onClick={() => handleImpersonateEmployee(emp)}
                              className="gap-2 cursor-pointer"
                              disabled={impersonatingId === emp.id}
                            >
                              <Shield className="h-4 w-4 text-emerald-600" />
                              <span>{impersonatingId === emp.id ? 'Starting Session...' : 'Impersonate'}</span>
                            </DropdownMenuItem>
                          ) : null}
                          <DropdownMenuItem onClick={() => openModal(emp, 'edit')} className="gap-2 cursor-pointer">
                            <Edit className="h-4 w-4 text-amber-500" /><span>Edit Employee</span>
                          </DropdownMenuItem>
                          
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            onClick={() => openModal(emp, 'delete')}
                            className="gap-2 cursor-pointer text-red-600 focus:text-red-600 focus:bg-red-50 dark:focus:bg-red-950"
                          >
                            <Trash2 className="h-4 w-4" /><span>Delete Employee</span>
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          {fetchError ? (
            <div className="text-center py-12">
              <p className="text-red-600">{fetchError}</p>
            </div>
          ) : filteredEmployees.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-muted-foreground">{loading ? 'Loading...' : 'No employees found matching your criteria.'}</p>
            </div>
          ) : null}
        </CardContent>
      </Card>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-sm text-muted-foreground">
          Showing page {pagination.page} of {pagination.totalPages} · {pagination.total} employees
        </p>
        <TablePagination page={page} totalPages={pagination.totalPages} onPageChange={setPage} />
      </div>
    </div>
  );
};

// ─── Exported pages ─────────────────────────────────────────────────────────
export const AllEmployees = () => <EmployeesTable />;

export const EmployeeActivityLogs = () => {
  const logs = [
    { id: 1, user: 'Priya Sharma', action: 'Updated lead status', target: 'Lead L001', timestamp: '2025-01-24 10:30' },
    { id: 2, user: 'Karthik Nair', action: 'Approved loan', target: 'Loan LN003', timestamp: '2025-01-24 09:45' },
    { id: 3, user: 'Anjali Mishra', action: 'Verified documents', target: 'Lead L004', timestamp: '2025-01-24 09:15' },
    { id: 4, user: 'Rohit Sinha', action: 'Created new agent', target: 'Agent A006', timestamp: '2025-01-23 16:20' },
  ];

  return (
    <div className="space-y-6" data-testid="activity-logs">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold font-['Plus_Jakarta_Sans']">Activity Logs</h1>
        <p className="text-muted-foreground mt-1">Track all employee activities</p>
      </div>
      <Card className="border border-border">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>User</TableHead>
                <TableHead>Action</TableHead>
                <TableHead>Target</TableHead>
                <TableHead>Timestamp</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {logs.map((log) => (
                <TableRow key={log.id} className="hover:bg-muted/30 transition-colors">
                  <TableCell className="font-medium">{log.user}</TableCell>
                  <TableCell>{log.action}</TableCell>
                  <TableCell className="font-mono text-sm">{log.target}</TableCell>
                  <TableCell className="text-muted-foreground">{log.timestamp}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};



export default AllEmployees;
